import React, { useEffect, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, LinearProgress,
  TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import LockIcon from "@mui/icons-material/Lock";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReplyIcon from "@mui/icons-material/Reply";
import BadgeIcon from "@mui/icons-material/Badge";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  getSdTicket,
  sdUpdateStatus,
  sdRouteToDistrict,
  sdResolve,
  sdStaffComment,
  sdReopenStaff,
} from "../../../../store/support-desk/actions";
import { useRequiredAuth } from "../../common/guard/requiredAuth";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  navy: "#0A1E3D", orange: "#F16522", orangeGrad: "linear-gradient(100deg,#F16522 0%,#F7941E 100%)",
  bg: "#EEF2F8", card: "#fff", ink: "#101B30", ink2: "#4A5A76", ink3: "#8494AD", line: "#E3E9F2",
  accentSoft: "#FDEDE2",
};

const STATUS_MAP = {
  submitted: { label: "Submitted",        color: "#2B5BC4", bg: "#EAF0FB" },
  review:    { label: "Under review",     color: "#B7791F", bg: "#FEF3E0" },
  district:  { label: "Sent to district", color: "#6B4EE0", bg: "#EEEAFB" },
  progress:  { label: "In progress",      color: "#1673C4", bg: "#E4F2FF" },
  resolved:  { label: "Resolved",         color: "#12885A", bg: "#E3F5EC" },
  reopened:  { label: "Reopened",         color: "#CC3A2E", bg: "#FDE9E7" },
};

// Fallback used only if the configurable categories fail to load.
const DEFAULT_CATEGORIES = [
  { key: "account",   name: "Login & account access",       color: "#2563EB" },
  { key: "transfer",    name: "Transfer",                     color: "#6B4EE0" },
  { key: "permutation", name: "Permutation",                  color: "#8B5CF6" },
  { key: "data",      name: "Data correction",              color: "#E8912B" },
  { key: "maternity",   name: "Maternity leave", color: "#12885A" },
  { key: "scholarship", name: "Scholarship",     color: "#0891B2" },
  { key: "cpd",         name: "CPD",             color: "#9333EA" },
  { key: "placement", name: "Placement & deployment",       color: "#1673C4" },
  { key: "bug",       name: "Technical bug / system error", color: "#E0453A" },
  { key: "other",     name: "Other / general enquiry",      color: "#5B6B85" },
];

const catByKey = (cats, key) => {
  const list = (cats && cats.length) ? cats : DEFAULT_CATEGORIES;
  return list.find(c => c.key === key) || list.find(c => c.key === "other") || list[list.length - 1];
};

// Compact list of a message's attachments with preview + download links.
function MsgAttachments({ items, dark }) {
  if (!items || !items.length) return null;
  const linkColor = dark ? "rgba(255,255,255,.85)" : "#4A5A76";
  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: .5 }}>
      {items.map(a => {
        const fileUrl = `/uploads/support-desk/${a.storedName}`;
        return (
          <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: .5, fontSize: 12, color: linkColor }}>
            <AttachFileIcon sx={{ fontSize: 14 }} />
            <a href={fileUrl} target="_blank" rel="noopener noreferrer"
               style={{ color: "inherit", textDecoration: "underline", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {a.originalName}
            </a>
            <a href={fileUrl} download={a.originalName} title="Download" style={{ color: "inherit", display: "inline-flex" }}>
              <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
            </a>
          </Box>
        );
      })}
    </Box>
  );
}

const FLOW = ["submitted", "review", "district", "progress", "resolved"];

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: T.ink2, bg: T.bg };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: "4px 12px", fontWeight: 700, fontSize: 13 }}>
      {s.label}
    </span>
  );
}

function TimeAgo({ iso }) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const m  = Math.floor(ms / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Action dialogs ─────────────────────────────────────────────────────────────

function RouteDialog({ open, onClose, onConfirm, loading }) {
  const [officerName, setOfficerName] = useState("");
  const [note,        setNote]        = useState("");
  const [err,         setErr]         = useState("");

  const submit = () => {
    if (!officerName.trim()) { setErr("District officer name is required."); return; }
    setErr("");
    onConfirm(officerName, note, () => { setOfficerName(""); setNote(""); onClose(); });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Route to district officer</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <TextField
          autoFocus required fullWidth label="District officer name" size="small"
          value={officerName} onChange={e => { setOfficerName(e.target.value); setErr(""); }}
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          fullWidth multiline rows={3} label="Note to district (optional)" size="small"
          value={note} onChange={e => setNote(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={submit} disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}>
          {loading ? "Routing…" : "Route to district"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Looks up one person's real TMIS record + current placement — auto, from the
// ticket's own National ID, or via an agent-entered employee ID (for a
// swap partner, who isn't on the ticket at all, or when the ticket's own NID
// doesn't match an account). Shared by the transfer and permutation panels
// below.
function CandidateLookup({ ticketId, autoFromTicket, title, onResolved }) {
  const [loading, setLoading]       = useState(!!autoFromTicket);
  const [err, setErr]               = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [candidate, setCandidate]   = useState(null);

  const lookup = (empId) => {
    setLoading(true); setErr(""); setCandidate(null);
    onResolved(null);
    axios.get(`/api/support-desk/tickets/${ticketId}/transfer-candidate`, { params: empId ? { employeeId: empId } : {} })
      .then(({ data }) => { setCandidate(data); onResolved(data); })
      .catch((e) => setErr(e?.response?.data || "Could not find this person's TMIS record."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (autoFromTicket) lookup(); }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ mb: 2 }}>
      {title && <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4A5A76", mb: 1 }}>{title}</Typography>}
      {loading && <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: .5 }}><CircularProgress size={16} /><Typography variant="body2">Looking up…</Typography></Box>}
      {!loading && !candidate && (
        <Box sx={{ display: "flex", gap: 1, mb: err ? 1 : 0 }}>
          <TextField size="small" label="Employee ID" value={employeeId} onChange={e => setEmployeeId(e.target.value)} sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={() => lookup(employeeId)} disabled={!employeeId.trim()}>Search</Button>
        </Box>
      )}
      {!loading && err && !candidate && <Alert severity="warning">{err}</Alert>}
      {!loading && candidate && (
        <Box sx={{ p: 1.5, borderRadius: 1.5, background: "#F6F8FC", border: `1px solid ${T.line}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{candidate.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Employee ID {candidate.employeeId} · {candidate.positionName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Currently at {[candidate.fromSchoolName, candidate.fromDistrictName].filter(Boolean).join(" / ")}
            </Typography>
          </Box>
          <Button size="small" onClick={() => { setCandidate(null); onResolved(null); }}>Change</Button>
        </Box>
      )}
    </Box>
  );
}

// "Resolve by transferring this person" — only offered on origin/destination
// categories when Global Settings > Support Desk has direct transfer
// resolution turned on. Looks the requester up, then lets the agent pick a
// real vacant destination post.
function TransferCandidatePanel({ ticketId, districts, value, onChange }) {
  const [candidate, setCandidate]   = useState(null);
  const [toDistrict, setToDistrict] = useState(null);
  const [vacancies, setVacancies]   = useState([]);
  const [vacLoading, setVacLoading] = useState(false);

  useEffect(() => {
    if (!toDistrict || !candidate) { setVacancies([]); return; }
    setVacLoading(true);
    axios.get(`/api/support-desk/tickets/vacancies/${toDistrict.id}`, { params: { positionId: candidate.positionId } })
      .then(({ data }) => setVacancies((data || []).filter(v => v.vacantBudgetPosts > 0)))
      .catch(() => setVacancies([]))
      .finally(() => setVacLoading(false));
  }, [toDistrict, candidate]);

  const pickPost = (sp) => {
    onChange(candidate && sp ? { teacherId: candidate.teacherId, toSchoolPositionId: sp.id } : null);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <CandidateLookup ticketId={ticketId} autoFromTicket onResolved={(c) => { setCandidate(c); onChange(null); }} />
      {candidate && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4A5A76", mb: 1 }}>TRANSFER TO</Typography>
          <Autocomplete
            size="small" value={toDistrict} options={districts || []}
            getOptionLabel={(o) => o.name || ""}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(e, v) => { setToDistrict(v || null); pickPost(null); }}
            renderInput={(params) => <TextField {...params} label="Destination district" required />}
            sx={{ mb: 2 }}
          />
          {toDistrict && (
            vacLoading ? <CircularProgress size={16} /> : (
              <Autocomplete
                size="small" value={value ? vacancies.find(v => v.id === value.toSchoolPositionId) || null : null}
                options={vacancies}
                getOptionLabel={(o) => [o.name, o.schoolName, o.sectorName].filter(Boolean).join(" — ")}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(e, v) => pickPost(v)}
                noOptionsText="No vacant post for this position in that district."
                renderInput={(params) => <TextField {...params} label="Vacant post" required />}
                sx={{ mb: 1 }}
              />
            )
          )}
        </>
      )}
    </Box>
  );
}

// "Resolve by permutating this person" — a genuine two-party swap: the
// requester and a partner (found by employee ID — they aren't on the ticket)
// each take over the other's current post. No destination picker needed;
// there's nothing vacant to pick, they're trading occupied posts.
function PermutationSwapPanel({ ticketId, onChange }) {
  const [one, setOne] = useState(null);
  const [two, setTwo] = useState(null);

  useEffect(() => {
    onChange(one && two ? { teacherId: one.teacherId, partnerTeacherId: two.teacherId } : null);
  }, [one, two]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ mb: 2 }}>
      <CandidateLookup ticketId={ticketId} autoFromTicket title="THE TICKET REQUESTER" onResolved={setOne} />
      <CandidateLookup ticketId={ticketId} title="SWAPPING WITH" onResolved={setTwo} />
      {one && two && one.teacherId === two.teacherId && (
        <Alert severity="error">The two people in a permutation must be different.</Alert>
      )}
    </Box>
  );
}

const TICKET_TYPES = [
  { key: "transfer", label: "Transfer" },
  { key: "permutation", label: "Permutation" },
];

function ResolveDialog({
  open, onClose, onConfirm, loading, resolutionType, categoryKey, districts, ticketId,
  allowTransferResolution, allowPermutationResolution,
}) {
  const structured = resolutionType === "origin_destination";
  const reclassifiable = TICKET_TYPES.some(t => t.key === categoryKey);
  const [ticketType, setTicketType] = useState(categoryKey);
  const [decision, setDecision] = useState("approve"); // "approve" | "deny" — deny always available, regardless of the toggles below
  const [note, setNote] = useState("");
  const [fromDistrict, setFromDistrict] = useState(null);
  const [fromSchool, setFromSchool]     = useState("");
  const [toDistrict, setToDistrict]     = useState(null);
  const [toSchool, setToSchool]         = useState("");
  const [transferPick, setTransferPick]     = useState(null);
  const [permutationPick, setPermutationPick] = useState(null);
  const [err,  setErr]  = useState("");

  // Not a free choice: when direct resolution is available for this ticket's
  // type, that's the only way to APPROVE it — the manual note/district
  // fallback only appears when there's no automatic mechanism to use instead.
  // Denying is always an option regardless (see `decision` above).
  const mode = ticketType === "transfer" && allowTransferResolution
    ? "transfer"
    : ticketType === "permutation" && allowPermutationResolution
    ? "permutation"
    : "note";

  useEffect(() => { if (open) { setTicketType(categoryKey); setDecision("approve"); } }, [open, categoryKey]);

  const reset = () => {
    setNote(""); setFromDistrict(null); setFromSchool(""); setToDistrict(null); setToSchool("");
    setTransferPick(null); setPermutationPick(null); setDecision("approve");
  };

  const submit = () => {
    if (structured && decision === "deny") {
      if (!note.trim()) { setErr("A reason is required to deny this request."); return; }
    } else if (structured && mode === "transfer") {
      if (!transferPick) { setErr("Pick who to transfer and a vacant destination post."); return; }
    } else if (structured && mode === "permutation") {
      if (!permutationPick) { setErr("Look up both people to swap."); return; }
    } else if (structured) {
      if (!fromDistrict || !toDistrict) { setErr("Select both the origin and destination district."); return; }
    } else if (!note.trim()) {
      setErr("Please enter a resolution summary."); return;
    }
    setErr("");
    const categoryPatch = reclassifiable && ticketType !== categoryKey ? { categoryKey: ticketType } : {};
    const payload = structured && decision === "deny"
      ? { ...categoryPatch, note, action: "deny" }
      : structured && mode === "transfer"
      ? { ...categoryPatch, note, action: "transfer", teacherId: transferPick.teacherId, toSchoolPositionId: transferPick.toSchoolPositionId }
      : structured && mode === "permutation"
      ? { ...categoryPatch, note, action: "permutation", teacherId: permutationPick.teacherId, partnerTeacherId: permutationPick.partnerTeacherId }
      : {
          ...categoryPatch,
          note,
          resFromDistrict: fromDistrict?.name || "",
          resFromSchool: fromSchool,
          resToDistrict: toDistrict?.name || "",
          resToSchool: toSchool,
        };
    onConfirm(payload, () => { reset(); onClose(); });
  };

  const districtField = (label, value, onChange) => (
    <Autocomplete
      size="small" value={value} options={districts || []}
      getOptionLabel={(o) => o.name || ""}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(e, v) => { onChange(v || null); setErr(""); }}
      renderOption={(props, o) => <Box component="li" {...props} key={o.id}>{o.name}</Box>}
      renderInput={(params) => <TextField {...params} label={label} required />}
      sx={{ mb: 2 }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Resolve this ticket</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        {reclassifiable && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4A5A76", mb: 1 }}>
              TICKET TYPE {ticketType !== categoryKey && "(will be updated on resolve)"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {TICKET_TYPES.map(tt => (
                <Button key={tt.key} size="small"
                  variant={ticketType === tt.key ? "contained" : "outlined"}
                  onClick={() => setTicketType(tt.key)}>
                  {tt.label}
                </Button>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .5 }}>
              This ticket was filed before Transfer and Permutation were separate categories — confirm which
              one it actually is so it's reported correctly.
            </Typography>
          </Box>
        )}

        {structured && (
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button size="small" variant={decision === "approve" ? "contained" : "outlined"} color="success" onClick={() => setDecision("approve")}>Approve</Button>
            <Button size="small" variant={decision === "deny" ? "contained" : "outlined"} color="error" onClick={() => setDecision("deny")}>Deny</Button>
          </Box>
        )}

        {structured && decision === "approve" && mode !== "note" && (
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "#12885A", mb: 1.5 }}>
            {mode === "transfer" ? "Resolving by transferring this person" : "Resolving by permutating this person"}
          </Typography>
        )}

        {structured && decision === "approve" && mode === "transfer" && open && (
          <TransferCandidatePanel ticketId={ticketId} districts={districts} value={transferPick} onChange={setTransferPick} />
        )}
        {structured && decision === "approve" && mode === "permutation" && open && (
          <PermutationSwapPanel ticketId={ticketId} onChange={setPermutationPick} />
        )}
        {structured && decision === "approve" && mode === "note" && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4A5A76", mb: 1 }}>WHERE THE TEACHER IS FROM</Typography>
            {districtField("From district", fromDistrict, setFromDistrict)}
            <TextField fullWidth size="small" label="From school (optional)"
              value={fromSchool} onChange={e => setFromSchool(e.target.value)} sx={{ mb: 3 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4A5A76", mb: 1 }}>WHERE THE TEACHER IS SENT</Typography>
            {districtField("To district", toDistrict, setToDistrict)}
            <TextField fullWidth size="small" label="To school (optional)"
              value={toSchool} onChange={e => setToSchool(e.target.value)} sx={{ mb: 2 }} />
          </Box>
        )}
        <TextField
          autoFocus={!structured || decision === "deny"} fullWidth multiline rows={structured && decision !== "deny" ? 2 : 4}
          required={!structured || decision === "deny"}
          label={structured ? (decision === "deny" ? "Reason for denial" : "Resolution note (optional)") : "Resolution summary"} size="small"
          helperText={
            structured
              ? (decision === "deny" ? "Explain why this request is being denied — the teacher will see this." : "Add any extra detail (optional).")
              : "Briefly explain how the issue was resolved."
          }
          value={note} onChange={e => { setNote(e.target.value); setErr(""); }}
          sx={{ mt: structured && decision !== "deny" ? 0 : 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" color={decision === "deny" ? "error" : "success"} onClick={submit} disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}>
          {loading ? "Resolving…"
            : decision === "deny" ? "Deny ticket"
            : mode === "transfer" ? "Transfer & resolve"
            : mode === "permutation" ? "Permutate & resolve"
            : "Mark as resolved"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function SupportTicket({
  sdTicket, sdLoading, supportDeskSettings,
  getSdTicket, sdUpdateStatus, sdRouteToDistrict, sdResolve, sdStaffComment, sdReopenStaff,
}) {
  const { id }        = useParams();
  const history       = useHistory();
  const { hasPermissions } = useRequiredAuth();

  const canManage = hasPermissions(["SUPPORT_DESK_MANAGE"]);

  const [commentText,   setCommentText]   = useState("");
  const [isInternal,    setIsInternal]    = useState(false);
  const [sending,       setSending]       = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRoute,     setShowRoute]     = useState(false);
  const [showResolve,   setShowResolve]   = useState(false);
  const [categories,    setCategories]    = useState(DEFAULT_CATEGORIES);
  const [districts,     setDistricts]     = useState([]);
  const [commentFiles,  setCommentFiles]  = useState([]);
  const commentFileRef = React.useRef();

  useEffect(() => {
    getSdTicket(id);
  }, [id]);

  useEffect(() => {
    axios.get("/api/support-desk/categories")
      .then(({ data }) => { if (data && data.length) setCategories(data); })
      .catch(() => {});
    axios.get("/api/support-desk/districts")
      .then(({ data }) => setDistricts(data || []))
      .catch(() => {});
  }, []);

  if (sdLoading && !sdTicket) {
    return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>;
  }

  if (!sdTicket) return null;

  const t    = sdTicket;
  const cat  = catByKey(categories, t.categoryKey);
  const timeline    = t.timeline || [];
  const comments    = t.comments || [];
  const attachments = (t.attachments || []).filter(a => !a.commentId);
  const flowDoneIdx = FLOW.indexOf(t.status);

  const refresh = () => getSdTicket(id);

  const handleStatusUpdate = (newStatus) => {
    setActionLoading(true);
    sdUpdateStatus(id, newStatus, "", () => { setActionLoading(false); refresh(); });
  };

  const handleRoute = (officerName, note, cb) => {
    setActionLoading(true);
    sdRouteToDistrict(id, officerName, note, () => { setActionLoading(false); refresh(); cb && cb(); });
  };

  const handleResolve = (payload, cb) => {
    setActionLoading(true);
    sdResolve(id, payload, () => { setActionLoading(false); refresh(); cb && cb(); });
  };

  const handleReopen = () => {
    setActionLoading(true);
    sdReopenStaff(id, () => { setActionLoading(false); refresh(); });
  };

  const handleComment = async () => {
    if (!commentText.trim() && !commentFiles.length) return;
    setSending(true);
    sdStaffComment(id, commentText, isInternal, commentFiles, () => {
      setCommentText("");
      setIsInternal(false);
      setCommentFiles([]);
      setSending(false);
      refresh();
    });
  };

  const addCommentFiles = (e) => {
    const added = Array.from(e.target.files || []);
    setCommentFiles(fs => [...fs, ...added].slice(0, 5));
    e.target.value = "";
  };

  const initials = (name = "") => name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight: "80vh" }}>
      {/* Back + Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/in/support-desk")} sx={{ color: T.ink2 }}>
          Inbox
        </Button>
        <Divider orientation="vertical" flexItem />
        <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: T.ink3 }}>{t.ref}</Typography>
        <StatusPill status={t.status} />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <IconButton onClick={refresh} size="small"><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {sdLoading && <LinearProgress sx={{ mb: 2 }} />}

      {(t.resFromDistrict || t.resToDistrict) && (
        <Box sx={{ background: "#E3F5EC", border: "1px solid #B7E4C7", borderRadius: 3, p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#12885A", textTransform: "uppercase", letterSpacing: ".08em", mb: .5 }}>
            Resolution — transfer outcome
          </Typography>
          <Typography sx={{ fontSize: 14, color: T.ink }}>
            From <b>{[t.resFromDistrict, t.resFromSchool].filter(Boolean).join(" / ") || "—"}</b>
            {"   →   "}
            To <b>{[t.resToDistrict, t.resToSchool].filter(Boolean).join(" / ") || "—"}</b>
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 2.5 }}>
        {/* ── Left: thread ────────────────────────────────────────────────── */}
        <Box>
          {/* Subject card */}
          <Box sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", p: 2.5, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: cat.color + "22", color: cat.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                {cat.name.slice(0, 2).toUpperCase()}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 17, color: T.ink }}>{t.subject}</Typography>
                <Typography sx={{ fontSize: 13, color: T.ink2 }}>{cat.name}</Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2, p: 2, background: "#f9fafb", borderRadius: 2 }}>
              <Typography sx={{ fontSize: 14, color: T.ink, whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{t.body}</Typography>
            </Box>
            {attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink2, mb: 1 }}>Attachments ({attachments.length})</Typography>
                {attachments.map(a => {
                  const fileUrl = `/uploads/support-desk/${a.storedName}`;
                  return (
                    <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, background: "#f1f5f9", borderRadius: 1, mb: .5 }}>
                      <AttachFileIcon sx={{ fontSize: 16, color: T.orange }} />
                      <Typography sx={{ fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.originalName}</Typography>
                      <Typography sx={{ fontSize: 11, color: T.ink3, ml: "auto" }}>
                        {a.fileSizeBytes ? `${Math.round(a.fileSizeBytes / 1024)} KB` : ""}
                      </Typography>
                      <Tooltip title="Preview">
                        <IconButton size="small" component="a" href={fileUrl} target="_blank" rel="noopener noreferrer" sx={{ color: T.ink2 }}>
                          <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton size="small" component="a" href={fileUrl} download={a.originalName} sx={{ color: T.ink2 }}>
                          <FileDownloadOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Thread */}
          <Box sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", p: 2.5, mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: ".09em", mb: 2.5 }}>
              Conversation &amp; Activity
            </Typography>

            {comments.length === 0 && (
              <Typography sx={{ color: T.ink3, fontSize: 13, py: 2, textAlign: "center" }}>
                No messages yet.
              </Typography>
            )}

            {comments.map(msg => {
              const isTeacher  = msg.fromType === "teacher";
              const isNoteType = msg.isInternal;
              return (
                <Box key={msg.id} sx={{ display: "flex", gap: 1.5, mb: 2.5, flexDirection: isTeacher ? "row-reverse" : "row" }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: isTeacher ? "linear-gradient(135deg,#F16522,#F7941E)" : isNoteType ? "#f3f4f6" : T.navy,
                    color: isNoteType ? T.ink3 : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 13, flexShrink: 0,
                  }}>
                    {initials(msg.authorName)}
                  </Box>
                  <Box sx={{ maxWidth: "76%" }}>
                    {isNoteType && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: .5, mb: .5 }}>
                        <LockIcon sx={{ fontSize: 11, color: "#D97706" }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#D97706" }}>
                          Internal note · not visible to teacher
                        </Typography>
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.ink3, mb: .5, textAlign: isTeacher ? "right" : "left" }}>
                      {msg.authorName}
                    </Typography>
                    <Box sx={{
                      background: isTeacher ? T.navy : isNoteType ? "#FFFBEB" : "#fff",
                      borderRadius: isTeacher ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
                      p: "10px 14px",
                      border: isTeacher ? "none" : isNoteType ? "1px dashed #D97706" : `1px solid ${T.line}`,
                    }}>
                      {msg.body && (
                        <Typography sx={{ fontSize: 14, color: isTeacher ? "#fff" : isNoteType ? "#92400E" : T.ink, lineHeight: 1.65 }}>
                          {msg.body}
                        </Typography>
                      )}
                      <MsgAttachments items={msg.attachments} dark={isTeacher} />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: T.ink3, mt: .5, textAlign: isTeacher ? "right" : "left" }}>
                      {msg.authorName} · <TimeAgo iso={msg.createdAt} />
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            <Divider sx={{ my: 2 }} />

            {/* Composer — pill tabs + full-width textarea + toolbar */}
            <Box sx={{ border: `1.5px solid ${T.line}`, borderRadius: 2.5, overflow: "hidden", background: isInternal ? "#FFFDF5" : T.card }}>
              {/* Tab pill buttons */}
              <Box sx={{ display: "flex", gap: 1, p: 1.5, pb: 1 }}>
                <button
                  onClick={() => setIsInternal(false)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: !isInternal ? T.navy : "transparent",
                    color: !isInternal ? "#fff" : T.ink3,
                    border: !isInternal ? "none" : `1.5px solid ${T.line}`,
                    borderRadius: 999, padding: "6px 14px",
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                  }}
                >
                  <ReplyIcon style={{ fontSize: 15 }} /> Reply to teacher
                </button>
                <button
                  onClick={() => setIsInternal(true)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: isInternal ? "#4B5563" : "transparent",
                    color: isInternal ? "#fff" : T.ink3,
                    border: isInternal ? "none" : `1.5px solid ${T.line}`,
                    borderRadius: 999, padding: "6px 14px",
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                  }}
                >
                  <LockIcon style={{ fontSize: 14 }} /> Internal note
                </button>
              </Box>
              {/* Text area */}
              <TextField
                fullWidth multiline rows={4} size="small"
                placeholder={isInternal ? "Write an internal note — only visible to staff…" : "Write a reply the teacher will see…"}
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleComment(); }}
                variant="standard"
                InputProps={{ disableUnderline: true, sx: { px: 1.5, pt: 1, pb: .5, fontSize: 14 } }}
              />
              {/* Selected files */}
              {commentFiles.length > 0 && (
                <Box sx={{ px: 1.5, pb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {commentFiles.map((f, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: .5, background: "#eef2f8", borderRadius: 999, px: 1.25, py: .25, fontSize: 12 }}>
                      <AttachFileIcon sx={{ fontSize: 14, color: T.orange }} />
                      <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      <span onClick={() => setCommentFiles(fs => fs.filter((_, j) => j !== i))}
                        style={{ cursor: "pointer", color: "#ef4444", fontWeight: 700 }}>×</span>
                    </Box>
                  ))}
                </Box>
              )}
              {/* Toolbar */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, borderTop: `1px solid ${T.line}`, background: "#fafafa" }}>
                <Box component="span"
                  onClick={() => commentFileRef.current?.click()}
                  sx={{ display: "inline-flex", alignItems: "center", gap: .5, color: T.ink3, fontSize: 13, fontWeight: 600, cursor: "pointer", "&:hover": { color: T.orange }, transition: "color .15s" }}>
                  <AttachFileIcon sx={{ fontSize: 17 }} /> Attach
                </Box>
                <input ref={commentFileRef} type="file" multiple hidden
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx" onChange={addCommentFiles} />
                <button
                  onClick={handleComment}
                  disabled={sending || (!commentText.trim() && !commentFiles.length)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: (sending || (!commentText.trim() && !commentFiles.length)) ? "#e5e7eb" : isInternal ? "#4B5563" : "linear-gradient(100deg,#F16522,#F7941E)",
                    color: (sending || (!commentText.trim() && !commentFiles.length)) ? "#9ca3af" : "#fff",
                    border: "none", borderRadius: 999, padding: "8px 18px",
                    fontWeight: 700, fontSize: 13,
                    cursor: (sending || (!commentText.trim() && !commentFiles.length)) ? "not-allowed" : "pointer",
                  }}
                >
                  {sending
                    ? <><CircularProgress size={13} style={{ color: "inherit" }} /> Sending…</>
                    : <><SendIcon style={{ fontSize: 15 }} /> Send reply</>}
                </button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Right: sidebar ───────────────────────────────────────────────── */}
        <Box>
          {/* Actions card */}
          {canManage && (
            <Box sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", p: 2.5, mb: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: ".09em", mb: 1.5 }}>Actions</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {t.status === "submitted" && (
                  <Button fullWidth variant="outlined" disabled={actionLoading} onClick={() => handleStatusUpdate("review")}
                    sx={{ borderRadius: 999, fontWeight: 700, textTransform: "none", py: 1.25, borderColor: T.line, color: T.ink }}>
                    Start reviewing
                  </Button>
                )}
                {["review", "reopened"].includes(t.status) && (
                  <>
                    <Button fullWidth variant="outlined" disabled={actionLoading} onClick={() => setShowRoute(true)}
                      sx={{ borderRadius: 999, fontWeight: 700, textTransform: "none", py: 1.25, borderColor: T.line, color: T.ink }}>
                      Route to district
                    </Button>
                    <Button fullWidth variant="outlined" disabled={actionLoading} onClick={() => handleStatusUpdate("progress")}
                      sx={{ borderRadius: 999, fontWeight: 700, textTransform: "none", py: 1.25, borderColor: T.line, color: T.ink }}>
                      Mark in progress
                    </Button>
                  </>
                )}
                {["review", "district", "progress", "reopened"].includes(t.status) && (
                  <Button fullWidth variant="outlined" disabled={actionLoading} startIcon={<CheckCircleIcon />} onClick={() => setShowResolve(true)}
                    sx={{ borderRadius: 999, fontWeight: 700, textTransform: "none", py: 1.25, borderColor: "#12885A", color: "#12885A", "&:hover": { background: "#E3F5EC" } }}>
                    Mark resolved
                  </Button>
                )}
                {t.status === "resolved" && (
                  <Button fullWidth variant="outlined" disabled={actionLoading} startIcon={<RefreshIcon />} onClick={handleReopen}
                    sx={{ borderRadius: 999, fontWeight: 700, textTransform: "none", py: 1.25, borderColor: T.line, color: T.ink2 }}>
                    {actionLoading ? "Reopening…" : "Reopen ticket"}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Requester card */}
          <Box sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", p: 2.5, mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: ".09em", mb: 2 }}>Requester</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: T.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
                {initials(t.teacherName)}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 15, color: T.ink, lineHeight: 1.2 }}>{t.teacherName}</Typography>
                {t.teacherCode && (
                  <Typography sx={{ fontSize: 12, color: T.ink3, fontFamily: "monospace" }}>{t.teacherCode}</Typography>
                )}
              </Box>
            </Box>
            {/* TMIS account status — helps the agent know if the teacher is already in service */}
            {(() => {
              const tm = t.tmisAccount;
              if (!tm) return null;
              const chip = tm.inService
                ? { label: "In service", color: "#12885A", bg: "#E3F5EC" }
                : tm.hasAccount
                  ? { label: "Has TMIS account", color: "#B7791F", bg: "#FEF3E0" }
                  : { label: "Not in TMIS", color: T.ink2, bg: T.bg };
              return (
                <Box sx={{ display: "inline-flex", alignItems: "center", px: 1.25, py: .5, borderRadius: 999, background: chip.bg, mb: 2 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: chip.color }}>{chip.label}</Typography>
                </Box>
              );
            })()}
            {[
              { Icon: BadgeIcon,       label: "NATIONAL ID",       val: t.teacherNid },
              { Icon: EmailIcon,       label: "EMAIL",             val: t.tmisAccount?.email },
              { Icon: WorkIcon,        label: "EMPLOYEE ID",       val: t.tmisAccount?.employeeId },
              { Icon: ApartmentIcon,   label: "SCHOOL",            val: t.teacherSchool },
              { Icon: LocationOnIcon,  label: "DISTRICT · SECTOR", val: [t.teacherDistrict, t.teacherSector].filter(Boolean).join(" · ") },
              { Icon: AccessTimeIcon,  label: "RAISED",            val: t.createdAt ? new Date(t.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "" },
              { Icon: PhoneIcon,       label: "PHONE",             val: t.teacherPhone },
            ].filter(r => r.val).map(({ Icon, label, val }) => (
              <Box key={label} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 1.5 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: "8px", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: .25 }}>
                  <Icon sx={{ fontSize: 16, color: T.ink3 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.ink3, letterSpacing: ".08em", textTransform: "uppercase", mb: .25 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink, wordBreak: "break-all" }}>{val}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Progress rail */}
          <Box sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", p: 2.5, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 2, color: T.ink, fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em" }}>Progress</Typography>
            {FLOW.map((s, i) => {
              const isDone    = t.status === "resolved" ? i <= flowDoneIdx : i < flowDoneIdx;
              const isActive  = i === flowDoneIdx && t.status !== "resolved";
              const isPending = !isDone && !isActive;
              const tEvent    = timeline.find(e => e.status === s);
              return (
                <Box key={s} sx={{ display: "flex", gap: 1.5, mb: 1.5, opacity: isPending ? .4 : 1 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <Box sx={{
                      width: 20, height: 20, borderRadius: "50%",
                      background: isDone ? "#12885A" : isActive ? T.orange : T.line,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {isDone && <CheckCircleIcon sx={{ color: "#fff", fontSize: 14 }} />}
                    </Box>
                    {i < FLOW.length - 1 && (
                      <Box sx={{ width: 2, flexGrow: 1, minHeight: 14, background: isDone ? "#12885A" : T.line, mt: .5 }} />
                    )}
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: isActive ? T.orange : T.ink }}>
                      {STATUS_MAP[s]?.label || s}
                    </Typography>
                    {tEvent && (
                      <>
                        <Typography sx={{ fontSize: 12, color: T.ink3 }}>
                          {tEvent.actorName} · <TimeAgo iso={tEvent.createdAt} />
                        </Typography>
                        {tEvent.note && (
                          <Typography sx={{ fontSize: 12, color: T.ink2, mt: .25 }}>{tEvent.note}</Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Meta */}
          <Box sx={{ background: T.card, borderRadius: 3, boxShadow: "0 2px 8px rgba(16,27,48,.07)", p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, color: T.ink, fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em" }}>Details</Typography>
            {[
              ["Ref",      t.ref],
              ["Priority", t.priorityKey?.toUpperCase()],
              ["Opened",   t.createdAt ? new Date(t.createdAt).toLocaleString() : "—"],
              ["SLA",      t.slaHrsLeft != null ? `${Math.round(t.slaHrsLeft)}h remaining` : t.resolvedAt ? "Resolved" : "—"],
              ["Assigned", t.assignedToId || "Unassigned"],
            ].map(([k, v]) => (
              <Box key={k} sx={{ display: "flex", gap: 1.5, mb: .75 }}>
                <Typography sx={{ fontSize: 12, color: T.ink3, minWidth: 70 }}>{k}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Dialogs */}
      <RouteDialog
        open={showRoute} loading={actionLoading}
        onClose={() => setShowRoute(false)}
        onConfirm={handleRoute}
      />
      <ResolveDialog
        open={showResolve} loading={actionLoading}
        onClose={() => setShowResolve(false)}
        onConfirm={handleResolve}
        resolutionType={cat?.resolutionType}
        categoryKey={t.categoryKey}
        districts={districts}
        ticketId={id}
        allowTransferResolution={canManage && !!supportDeskSettings?.allowTransferResolution}
        allowPermutationResolution={canManage && !!supportDeskSettings?.allowPermutationResolution}
      />
    </Box>
  );
}

const mapStateToProps = ({ sdTicket, sdLoading, supportDeskSettings }) => ({ sdTicket, sdLoading, supportDeskSettings });
export default connect(mapStateToProps, {
  getSdTicket, sdUpdateStatus, sdRouteToDistrict, sdResolve, sdStaffComment, sdReopenStaff,
})(SupportTicket);
